import { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Facebook, Instagram, ExternalLink, Landmark } from "lucide-react";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { bnNum } from "@/constants/site";
import { useSite } from "@/lib/site";
import { useLang } from "@/lib/lang";

export default function ContactSection() {
  const SITE = useSite();
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await API.post("/messages", form);
      toast.success(t("con_success"));
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="py-20 sm:py-28 bg-[#1E0A0E]/40 border-y border-[#F2B134]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal className="max-w-2xl">
          <Eyebrow>{t("con_eyebrow")}</Eyebrow>
          <h2 className="font-bengali font-extrabold text-3xl sm:text-5xl mt-4">
            {t("con_title_a")} <span className="gold-text">{t("con_title_b")}</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-10 items-start">
          <Reveal className="space-y-4">
            <div className="rounded-2xl border border-[#F2B134]/20 bg-[#120507] p-6 flex gap-4">
              <MapPin className="text-[#F2B134] shrink-0 mt-1" size={20} />
              <div>
                <p className="font-bengali font-bold">{SITE.addressBN}</p>
                <p className="text-sm text-[#D1BBA7]/80 mt-1">{t("con_landmark")} {SITE.landmark}</p>
                <a href={SITE.mapUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-map-link" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F2B134] mt-2 hover:underline">
                  {t("con_map")} <ExternalLink size={11} />
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-[#F2B134]/20 bg-[#120507] p-6 flex gap-4">
              <Phone className="text-[#F2B134] shrink-0 mt-1" size={20} />
              <div>
                <p className="font-bengali font-bold">{t("con_phone")}</p>
                <p className="tabular-nums text-[#D1BBA7] mt-1">{SITE.phones.map((p) => `+91 ${p}`).join(" · ")}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-[#F2B134]/20 bg-[#120507] p-6 flex gap-4">
              <MessageCircle className="text-[#F2B134] shrink-0 mt-1" size={20} />
              <div>
                <p className="font-bengali font-bold">{t("con_wa")}</p>
                <p className="tabular-nums text-[#D1BBA7] mt-1">{SITE.whatsapp.map((p) => `+91 ${p}`).join(" · ")}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-[#F2B134]/20 bg-[#120507] p-6 flex gap-4">
              <Mail className="text-[#F2B134] shrink-0 mt-1" size={20} />
              <div className="min-w-0">
                <p className="font-bengali font-bold">{t("con_email")}</p>
                <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="text-[#D1BBA7] text-sm break-all hover:text-[#F2B134]">{SITE.email}</a>
              </div>
            </div>

            <div className="flex gap-3">
              <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-link" className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-[#F2B134]/25 py-4 text-sm font-bold text-[#D1BBA7] hover:text-[#F2B134] hover:border-[#F2B134]/60 transition-colors">
                <Facebook size={16} /> {t("con_fb")}
              </a>
              <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-[#F2B134]/25 py-4 text-sm font-bold text-[#D1BBA7] hover:text-[#F2B134] hover:border-[#F2B134]/60 transition-colors">
                <Instagram size={16} /> {t("con_ig")}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl border border-[#F2B134]/20 bg-[#120507] p-6 sm:p-8 spotlight" data-testid="contact-form">
              <h3 className="font-bengali font-bold text-xl flex items-center gap-3">
                <Landmark size={18} className="text-[#F2B134]" /> {t("con_form_t")}
              </h3>
              <div className="mt-6 space-y-4">
                <input
                  required
                  data-testid="input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("con_name_ph")}
                  className="w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-3 text-[#FDF6EA] placeholder:text-[#D1BBA7]/50 outline-none focus:border-[#F2B134]/70 transition-colors"
                />
                <input
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder={t("con_phone_ph")}
                  className="w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-3 text-[#FDF6EA] placeholder:text-[#D1BBA7]/50 outline-none focus:border-[#F2B134]/70 transition-colors"
                />
                <textarea
                  required
                  data-testid="input-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={t("con_msg_ph")}
                  className="w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-3 text-[#FDF6EA] placeholder:text-[#D1BBA7]/50 outline-none focus:border-[#F2B134]/70 transition-colors resize-none font-bengali"
                />
                <button type="submit" disabled={busy} data-testid="contact-submit-btn" className="btn-gold w-full rounded-xl py-3.5 font-bold disabled:opacity-50">
                  {busy ? t("con_sending") : t("con_submit")}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
